/**
 * Step 1 of the Upload Wizard — drag-and-drop CSV upload + 5-row preview.
 *
 * The drop zone has 5 visible states (idle / hover / uploading / success
 * / error). Validation mirrors the BE's `validate_headers` contract in
 * `golab_csv_utils.py`: the CSV MUST carry `verbatim`, `channel`, AND
 * `date` columns (case-insensitive, no aliases). `journey` and `product`
 * are optional. Catching this client-side stops users from advancing
 * through three more wizard steps only to hit a 400 on Submit.
 */

import React, { useCallback, useRef, useState } from 'react';
import Papa from 'papaparse';
import { FileText, Upload as UploadIcon, X as XIcon } from 'lucide-react';

import { Button } from '../../../../ui/button';
import { cn } from '../../../../../lib/utils';
import CsvFormatGuideDialog from './CsvFormatGuideDialog';

export interface ParseResult {
  headers: string[];
  previewRows: Record<string, string>[];
  totalCount: number;
  fileSizeBytes: number;
}

// Mirrors BE `golab_csv_utils.REQUIRED_HEADERS`. Match exactly (case-
// insensitive); no aliases — the BE's parser doesn't accept any.
export const REQUIRED_COLUMNS: readonly string[] = ['verbatim', 'channel', 'date'];

/**
 * Validate that the CSV's header row contains every required column.
 * Returns `null` when valid, or an error message naming what's missing.
 * Exported for unit testing.
 */
export const validateHeaders = (headers: string[]): string | null => {
  const present = new Set(headers.map((h) => h.trim().toLowerCase()));
  const missing = REQUIRED_COLUMNS.filter((c) => !present.has(c));
  if (missing.length === 0) return null;
  const cols = missing.map((c) => `\`${c}\``).join(', ');
  const plural = missing.length === 1 ? 'column' : 'columns';
  return `Missing required ${plural}: ${cols}. The BE requires \`verbatim\`, \`channel\`, and \`date\` (case-insensitive).`;
};

const isCsv = (file: File): boolean =>
  /\.csv$/i.test(file.name) || file.type === 'text/csv';

// Mirrors the BE cap in `golab_dataset_service.ingest_upload`. Reject
// over-size files on selection rather than letting the user reach
// Submit and wait for a 413 round-trip.
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface Props {
  file: File | null;
  parseResult: ParseResult | null;
  onFileSelected: (file: File, result: ParseResult) => void;
  onClear: () => void;
}

type DropState = 'idle' | 'hover' | 'parsing' | 'error';

const Step1UploadCsv: React.FC<Props> = ({ file, parseResult, onFileSelected, onClear }) => {
  const [state, setState] = useState<DropState>('idle');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (selected: File) => {
      if (!isCsv(selected)) {
        setState('error');
        setError('Only CSV files are supported.');
        return;
      }
      if (selected.size > MAX_UPLOAD_BYTES) {
        setState('error');
        setError(
          `File is ${formatBytes(selected.size)}; the maximum upload size is ${formatBytes(
            MAX_UPLOAD_BYTES,
          )}.`,
        );
        return;
      }
      setState('parsing');
      setError(null);
      // Stream the CSV row-by-row instead of buffering it in memory.
      // Multi-MB CSVs would otherwise block the main thread (synchronous
      // parse) or balloon RAM (`result.data` holds every row). The `step`
      // callback receives one row at a time; we keep only the first 5
      // for preview and a running counter for the total.
      const previewRows: Record<string, string>[] = [];
      let totalCount = 0;
      let headers: string[] = [];
      let headerError: string | null = null;
      let aborted = false;

      Papa.parse<Record<string, string>>(selected, {
        header: true,
        skipEmptyLines: true,
        worker: true,
        step: (results, parser) => {
          if (aborted) return;
          // First row in: cache headers + run header validation. Abort
          // the stream early if the required column is missing — no
          // point parsing the rest of a malformed file.
          if (totalCount === 0) {
            headers = results.meta.fields ?? [];
            headerError = validateHeaders(headers);
            if (headerError) {
              aborted = true;
              parser.abort();
              return;
            }
          }
          if (previewRows.length < 5) {
            previewRows.push(results.data);
          }
          totalCount += 1;
        },
        complete: () => {
          if (headerError) {
            setState('error');
            setError(headerError);
            return;
          }
          if (totalCount === 0) {
            // Empty CSV (no data rows). Treat as error rather than
            // letting an empty upload slip through.
            setState('error');
            setError('CSV contains no data rows.');
            return;
          }
          onFileSelected(selected, {
            headers,
            previewRows,
            totalCount,
            fileSizeBytes: selected.size,
          });
          setState('idle');
        },
        error: (err) => {
          setState('error');
          setError(err.message || 'Failed to parse CSV.');
        },
      });
    },
    [onFileSelected],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (state !== 'parsing') setState('hover');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (state === 'hover') setState('idle');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setState('idle');
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
  };

  const handleClickBrowse = () => {
    inputRef.current?.click();
  };

  const handleClear = () => {
    onClear();
    setState('idle');
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  // The "CSV format guide" affordance lives in a shared header so it's
  // reachable in both the pre-parse (drop zone) and post-parse (preview)
  // states. Right-aligned above the step content.
  const header = (
    <div className="flex items-center justify-end">
      <CsvFormatGuideDialog />
    </div>
  );

  // Already-parsed: show preview + status line + remove affordance.
  if (parseResult && file) {
    return (
      <div className="space-y-4" data-testid="step1-preview">
        {header}
        <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-emerald-600" aria-hidden="true" />
            <div className="text-[13px]">
              <div className="font-semibold text-foreground">{file.name}</div>
              <div className="text-muted-foreground">
                {parseResult.totalCount.toLocaleString()} records ·{' '}
                {formatBytes(parseResult.fileSizeBytes)}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            data-testid="step1-clear"
          >
            <XIcon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Remove file</span>
          </Button>
        </div>
        <PreviewTable result={parseResult} />
      </div>
    );
  }

  // Pre-parse: drop zone in one of its visible states.
  return (
    <div className="space-y-3">
      {header}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClickBrowse}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClickBrowse();
          }
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-12 text-center transition',
          state === 'idle' && 'border-border/70 bg-card/40 hover:border-border hover:bg-card',
          state === 'hover' && 'border-primary bg-accent/40',
          state === 'parsing' && 'border-primary/40 bg-card/40',
          state === 'error' && 'border-red-400 bg-red-50/60',
        )}
        data-testid="step1-dropzone"
        data-state={state}
        aria-label="Upload CSV file (click or drag and drop)"
      >
        <UploadIcon className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
        <div className="space-y-1">
          <div className="text-sm font-semibold text-foreground">
            {state === 'parsing' ? 'Parsing CSV…' : 'Drop CSV here, or click to browse'}
          </div>
          <div className="text-[12px] text-muted-foreground">
            Required: <code>verbatim</code>, <code>channel</code>,{' '}
            <code>date</code>. Optional: <code>journey</code>,{' '}
            <code>product</code>.
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleInputChange}
          data-testid="step1-file-input"
        />
      </div>
      {state === 'error' && error && (
        <p
          className="rounded-md border-l-4 border-red-600 bg-red-50 p-3 text-[12px] text-red-900"
          data-testid="step1-error"
        >
          {error}
        </p>
      )}
    </div>
  );
};

const PreviewTable: React.FC<{ result: ParseResult }> = ({ result }) => {
  const { headers, previewRows } = result;
  // Lead with `verbatim` (case-insensitive) since the BE requires it as
  // the canonical content column. Then surface up to three additional
  // headers (channel, journey, product, date — whichever the file
  // carries) so the preview is meaningful without overflowing.
  const verbatimCol =
    headers.find((h) => /^verbatim$/i.test(h.trim())) ?? headers[0];
  const otherCols = headers
    .filter((h) => h !== verbatimCol)
    .slice(0, 3);
  const cols = [verbatimCol, ...otherCols].filter(Boolean);
  return (
    <div className="overflow-x-auto rounded-md border border-border" data-testid="step1-preview-table">
      <table className="w-full text-[12px]">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Row</th>
            {cols.map((c) => (
              <th
                key={c}
                className="px-3 py-2 text-left font-semibold text-muted-foreground"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {previewRows.map((row, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-3 py-2 tabular-nums text-muted-foreground">{i + 1}</td>
              {cols.map((c) => (
                <td key={c} className="max-w-[280px] truncate px-3 py-2">
                  {row[c] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Step1UploadCsv;
